import "./Heading.css";
import {
	MaterialSymbol,
	type MaterialSymbolRelativeSize,
} from "@ui/MaterialSymbol";
import { HStack } from "@ui/Stack";
import type { TextColor, TextWeight } from "@ui/typography/Text";
import { smartenChildren } from "@utils/text";
import type { JSX, ParentComponent } from "solid-js";
import { children } from "solid-js";
import { Text } from "./Text";

export type HeadingProps = {
	/**
	 * Controls font size and weight.
	 * - h1: 1.5rem / 24px / bold
	 * - h2: 1.25rem / 20px / bold
	 * - h3: 1rem / 16px / bold
	 * - h4: 0.9375rem / 15px / bold
	 */
	as?: "h1" | "h2" | "h3" | "h4";
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	weight?: Extract<TextWeight, "semi-bold" | "bold" | "extra-bold">;
	ellipsis?: boolean;
	center?: boolean;
	/**
	 * Applies typographic smart quotes and em dashes when children is a plain string.
	 * @default false
	 */
	smarten?: boolean;
	/** @default "body" */
	family?: "display" | "body";
};

export const Heading: ParentComponent<HeadingProps> = (props) => {
	return (
		<Text
			as={props.as ?? "h2"}
			class={`gvtui-heading ${props.class ?? ""}`}
			classList={{
				...props.classList,
				[`gvtui-heading--weight-${props.weight}`]: props.weight !== undefined,
			}}
			style={props.style}
			family={props.family ?? "body"}
			color="ink"
			ellipsis={props.ellipsis}
			center={props.center}
			smarten={props.smarten}
			weight={props.weight ?? "bold"}
		>
			{props.children}
		</Text>
	);
};

export type HeroHeadingProps = Omit<HeadingProps, "as">;

/** Hero page title: always `h1`. */
export const HeroHeading: ParentComponent<HeroHeadingProps> = (props) => {
	return (
		<Text
			as="h1"
			class={`gvtui-heading gvtui-heading--hero-heading ${props.class ?? ""}`.trim()}
			classList={{
				...props.classList,
				[`gvtui-heading--weight-${props.weight}`]: props.weight !== undefined,
			}}
			style={props.style}
			family="display"
			color="ink"
			ellipsis={props.ellipsis}
			smarten={props.smarten}
			weight={props.weight ?? "bold"}
		>
			{props.children}
		</Text>
	);
};

export const HeavyHeading: ParentComponent<HeroHeadingProps> = (props) => {
	return (
		<Text
			as="h1"
			class={`gvtui-heading gvtui-heading--heavy-heading ${props.class ?? ""}`.trim()}
			classList={{
				...props.classList,
				[`gvtui-heading--weight-${props.weight}`]: props.weight !== undefined,
			}}
			style={props.style}
			color="ink"
			ellipsis={props.ellipsis}
			smarten={props.smarten}
			weight={props.weight ?? "bold"}
		>
			{props.children}
		</Text>
	);
};

export type SymbolHeadingProps = {
	as?: "h1" | "h2" | "h3" | "h4";
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	symbol: string;
	/** Color of the leading symbol. Same tokens as {@link Text} `color`. @default "fg2" */
	symbolColor?: TextColor;
	symbolRelativeSize?: MaterialSymbolRelativeSize;
	symbolClass?: string;
	/**
	 * Applies typographic smart quotes and em dashes when children is a plain string.
	 * @default false
	 */
	smarten?: boolean;
	/** Same tokens as {@link Text} `color`. @default "ink" */
	color?: TextColor;
};

export const SymbolHeading: ParentComponent<SymbolHeadingProps> = (props) => {
	const resolved = children(() => props.children);

	return (
		<Text
			as={props.as ?? "h2"}
			class={`gvtui-heading ${props.class ?? ""}`}
			classList={props.classList}
			style={props.style}
			color={props.color ?? "ink"}
		>
			<HStack spacing="medium" align="center">
				<MaterialSymbol
					relativeSize={props.symbolRelativeSize}
					symbol={props.symbol}
					color={props.symbolColor ?? "fg2"}
					class={props.symbolClass}
				/>
				<div>
					{props.smarten === true
						? (smartenChildren(resolved()) as JSX.Element)
						: resolved()}
				</div>
			</HStack>
		</Text>
	);
};
